import styled, { css } from 'styled-components'

/**
 * Shared page-title type for routes/groups that live under the platform nav
 * (tab-group titles like Mailboxes/Content Catalog/Incidents/Settings, shown
 * only when NEW_PLATFORM_NAV is on): <h3> + Poppins 20 / 600 / 20.
 * Line-height matches the font so the title doesn't sit 4px lower than the
 * collapse toggle / top-bar band. Use PageTitleH3 for those, PageHeaderSimple
 * for every other (old-nav / nav-agnostic) bare title, or PageHeader when the
 * heading also wraps extra inline content (date range, etc.).
 */
export const pageTitleCss = css`
  font-family: Poppins, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 20px;
  color: var(--color-text);
  margin: 0;
  padding: 0;
`

// New-platform-nav tab-group title (Mailboxes, Content Catalog, Incidents,
// Settings group titles, ...) — call sites that only render when
// NEW_PLATFORM_NAV is on, plus pages whose titles were deliberately redesigned
// to this spec on the nav branch (PSIMA, SOC agent, deepfake). Do not repoint
// other pages to this: PageHeaderSimple below is the default for everything
// else and must keep Prod's look.
export const PageTitleH3 = styled.h3`
  ${pageTitleCss}
`

// Renders on old-nav pages (dashboard, mailboxes, resolutionCenter,
// awareness-center, campaigns, dmarc, etc.) and on pages that render the same
// way regardless of nav — must keep Prod's `.typography-h2` look. Only the
// confirmed new-nav-only tab-group titles use PageTitleH3 instead; do not
// restyle this one for a new-nav call site.
export const PageHeaderSimple = ({ children }) => <span className="typography-h2">{children}</span>

// Prod (old-nav) rendering is a .typography-h2 div that IGNORES the caller's
// className/style — callers like the dashboards pass nothing, and forwarding a
// className would let legacy global rules (.card h3.title etc.) restyle titles
// that never matched them on Prod. Only under the new platform nav does the
// shared 20/600 page-title spec apply (GlobalFilters' scoped rules rely on it).
const PageHeaderDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .platform-nav-content & {
    ${pageTitleCss}
  }
`

const PageHeader = ({ children }) => (
  <PageHeaderDiv className="typography-h2">{children}</PageHeaderDiv>
)

export default PageHeader
